/*
 * File: JournalResponseWidget.jsx
 * Project: lets-talk-web
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Let's Talk. All rights reserved.
 *
 * Last Modified: September 26, 2024 at 9:33 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useState, useRef, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import validator from 'validator';
import { useDispatch, useSelector } from 'react-redux';
import { Howl } from 'howler';

// Utilities
import { toastError } from '../../utilities/toaster';

// Services
import { fetchJournalEntries, submitJournalResponse } from '../../services/journal';

// Slices
import { updateJournalEntries } from '../../store/slices/journal/journal.slice';

// Components
import { Button } from '../Button';
import { Typography } from '../Typography';
import { TextArea } from '../TextArea';
import { ConfettiBurst } from '../ConfettiBurst';

// Styles
import * as S from './JournalResponseWidget.styles';


/**
 * Component
 */

export const JournalResponseWidget = ({
	className, entry, handleComplete, handleNewResponse, isOpen
}) => {

	// Create state handlers
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fireConfetti, setFireConfetti] = useState(false);
	const [contentError, setContentError] = useState(null);
	const [inputValues, setInputValues] = useState({});
	const [showConfirmation, setShowConfirmation] = useState(false);

	// Get journal from hook
	const journalPrompts = useSelector((state) => state.journal.prompts.value);

	// Use hooks
	const dispatch = useDispatch();

	// Create refs
	const confettiTargetRef = useRef();

	// Create sound hooks
	const fanfareSound = useMemo(() => new Howl({
		src: [`${process.env.CDN_URL}/public/assets/audio/fanfare.mp3`],
		html5: true,
		volume: 0.1
	}), []);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Handle submit action
	const handleSubmit = async () => {

		// Get parameters
		const { content } = inputValues;

		// Validate parameters
		if (!content || validator.isEmpty(content, { ignore_whitespace: true })) {
			setContentError({ message: 'Please enter a response for your child before updating your journal' });
			return;
		}

		// Update submission state
		setIsSubmitting(true);

		// Submit entry
		try {

			// Submit journal entry
			await submitJournalResponse({ response: content, entryId: entry.id });

			// Fetch journal entries
			const { data: { entries } } = await fetchJournalEntries();
			dispatch(updateJournalEntries(entries));

			// Update submission state
			setIsSubmitting(false);

			// Trigger handle new response
			if (handleNewResponse) handleNewResponse(content);

			// Show confirmation
			setShowConfirmation(true);

			// Fire confetti
			setFireConfetti(true);

			// Reset confetti
			setTimeout(() => {
				setFireConfetti(false);
			}, 4000);

		} catch (e) {

			// Update submission state
			setIsSubmitting(false);

			// Show error message
			toastError(uiMode, 'Whoops. We\'re having trouble saving your journal response. Give it another go.', { duration: 7000 });
		}
	};

	// Handle complete action
	const handleCompleteAction = (responseAdded) => {

		// Reset state
		setShowConfirmation(false);
		setInputValues({});

		// Handle complete
		if (handleComplete) handleComplete(responseAdded);
	};

	// Handle on input change action
	const handleOnChange = (event) => {
		const {
			name, value
		} = event.target;
		setInputValues({ ...inputValues, [name]: value });
	};

	// Handle update on component visibility
	useEffect(() => {
		if (isOpen) {
			setInputValues({ content: entry?.response || '' });
		}
	}, [isOpen]);

	// Handle component initialization
	useEffect(() => {}, []);

	// Render component
	return (
		<S.JournalResponseWidget className={className}>

			{/* Close Button */}
			<S.CloseButton icon={['fal', 'times']} size={1.1} onClick={() => { handleCompleteAction(false); }} />

			{/* Submission Panel */}
			{!showConfirmation && (
				<S.SubmissionPanel>

					{/* Content */}
					<S.PromptContainer>
						<Typography tag="p" variation="1" weight="light" className="subtitle">
							Write your thoughts below to share with your child.
						</Typography>
					</S.PromptContainer>

					{/* Text Area */}
					<TextArea
						placeholder={(() => {
							const prompt = journalPrompts?.find(({ id }) => id === entry.promptId);
							return prompt ? prompt.prompt : entry.prompt;
						})()}
						name="content"
						type="text"
						rows={12}
						error={contentError}
						value={inputValues.content || ''}
						containerClassName="formInput"
						onFocus={() => { setContentError(null); }}
						onKeyUp={() => { setContentError(null); }}
						onBlur={() => { setContentError(null); }}
						onChange={handleOnChange}
					/>

					{/* Actions */}
					<S.ActionContainer>
						<Button disabled={isSubmitting} isLoading={isSubmitting} variant="solid" size="medium" onClick={handleSubmit}>
							<Typography tag="p" variant="2" weight="medium">Share with Child</Typography>
						</Button>
						<S.TextAreaPadding />
					</S.ActionContainer>

				</S.SubmissionPanel>
			)}

			{/* Confirmation Panel */}
			{showConfirmation && (
				<S.ConfirmationPanel>

					{/* Icon */}
					<S.ConfirmationIconContainer>
						<FontAwesomeIcon icon={['fal', 'check-circle']} />
						<S.ConfettiTarget ref={confettiTargetRef} />
					</S.ConfirmationIconContainer>

					{/* Title */}
					<Typography tag="h3" weight="bold">You&apos;ve created a new journal entry!</Typography>

					{/* Instructions */}
					<Typography tag="p" variation="1" weight="medium">
						Thank you for sharing your thoughts. We&apos;ll share your response with your child in their journal.
					</Typography>

					{/* Next Button */}
					<Button onClick={() => { handleCompleteAction(true); }} size="large">Complete</Button>

				</S.ConfirmationPanel>
			)}

			{/* Confetti */}
			{fireConfetti && (
				<ConfettiBurst
					fireAway={fireConfetti}
					targetRef={confettiTargetRef}
					force={0.4}
					duration={3000}
					particleCount={80}
					className="zIndex3"
					audioHook={fanfareSound}
				/>
			)}
			{showConfirmation && <S.ConfirmationBackground />}
		</S.JournalResponseWidget>
	);
};


/**
 * Configuration
 */

JournalResponseWidget.displayName = 'JournalResponseWidget';
JournalResponseWidget.propTypes = {
	className: PropTypes.string,
	handleComplete: PropTypes.func,
	handleNewResponse: PropTypes.func,
	entry: PropTypes.shape(),
	isOpen: PropTypes.bool
};
JournalResponseWidget.defaultProps = {
	className: null,
	handleComplete: null,
	handleNewResponse: null,
	entry: null,
	isOpen: false
};
